import {
	type BulkGeneratePromoCodeRequest,
	type PromoCode,
	apiAppBusinessPromocodeList,
	apiAppBusinessPromocodeUpsert,
	apiAppBusinessPromocodeDelete,
	apiAppBusinessPromocodeDeletebulk,
	apiAppBusinessPromocodeToggleactivebulk,
	apiAppBusinessPromocodeBulkgenerate
} from '#lib/types/generated/index.js';
import { toSavePromoCodeRequest } from '#lib/helper/promoCode.js';
import { optimisticDelete, optimisticSave } from '#lib/stores/optimistic.js';
import { get, writable } from 'svelte/store';

export const promoCodeStore = writable<PromoCode[]>([]);

export function reload(): Promise<void> {
	return apiAppBusinessPromocodeList().then((res) => {
		promoCodeStore.set(res.items);
	});
}

export function savePromoCode(promoCode: PromoCode): Promise<PromoCode> {
	return optimisticSave(promoCodeStore, promoCode, () =>
		apiAppBusinessPromocodeUpsert(promoCode.id, toSavePromoCodeRequest(promoCode))
	);
}

export function deletePromoCode(promoCode: PromoCode): Promise<void> {
	return optimisticDelete(promoCodeStore, promoCode, () =>
		apiAppBusinessPromocodeDelete(promoCode.id)
	);
}

export function getPromoCodeById(id: string): PromoCode | undefined {
	return get(promoCodeStore).find((p) => p.id === id);
}

export function upsertPromoCode(promoCode: PromoCode): void {
	promoCodeStore.update((codes) => {
		const index = codes.findIndex((p) => p.id === promoCode.id);
		if (index !== -1) {
			codes[index] = promoCode;
			return [...codes];
		}
		return [...codes, promoCode];
	});
}

export function deletePromoCodes(ids: string[]): Promise<void> {
	const backup = get(promoCodeStore).filter((p) => ids.includes(p.id));
	promoCodeStore.update((codes) => codes.filter((p) => !ids.includes(p.id)));

	return apiAppBusinessPromocodeDeletebulk({ ids }).catch((error) => {
		promoCodeStore.update((codes) => [...codes, ...backup]);
		throw error;
	});
}

export function bulkGeneratePromoCodes(
	payload: BulkGeneratePromoCodeRequest
): Promise<PromoCode[]> {
	return apiAppBusinessPromocodeBulkgenerate(payload).then((res) => {
		promoCodeStore.update((codes) => [...codes, ...res.items]);
		return res.items;
	});
}

export function toggleActivePromoCodes(ids: string[], isActive: boolean): Promise<void> {
	const backup = get(promoCodeStore);
	promoCodeStore.update((codes) =>
		codes.map((code) => (ids.includes(code.id) ? { ...code, isActive } : code))
	);

	return apiAppBusinessPromocodeToggleactivebulk({ ids, isActive }).catch((error) => {
		promoCodeStore.set(backup);
		throw error;
	});
}
